import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { AdminLogin } from "../components/admin/AdminLogin";
import { CarForm } from "../components/admin/CarForm";
import { CarPhoto } from "../components/CarCard";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { adminApi, errMsg, getToken, setToken } from "../lib/admin";
import { carTitle, fetchCars, formatPrice } from "../lib/company";

const Dashboard = ({ onLogout }) => {
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const load = useCallback(() => { fetchCars().then(setCars); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async () => {
    try { await adminApi.delete(`/cars/${toDelete.id}`); toast.success("Auto eliminata"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setToDelete(null);
  };

  return (
    <main className="pt-[100px] pb-24 max-w-[1200px] mx-auto px-5 md:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)]">Area riservata</p>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">Le tue auto <span className="text-[var(--muted)] text-3xl">({cars.length})</span></h1>
        </div>
        <div className="flex gap-3">
          <button onClick={onLogout} data-testid="admin-logout-btn" className="h-12 px-5 rounded-full border border-[var(--line)] bg-white text-sm font-semibold inline-flex items-center gap-2 hover:border-[var(--ink)]"><LogOut className="w-4 h-4" /> Esci</button>
          <button onClick={() => setEditing({})} data-testid="admin-add-car-btn" className="h-12 px-6 rounded-full bg-[var(--red)] text-white text-sm font-semibold inline-flex items-center gap-2 hover:bg-[var(--red-dark)]"><Plus className="w-4 h-4" /> Aggiungi auto</button>
        </div>
      </div>
      {cars.length === 0 ? (
        <div className="rounded-[28px] border border-dashed border-[var(--line)] p-12 text-center text-[var(--muted)]" data-testid="admin-empty">Nessuna auto ancora. Clicca “Aggiungi auto” per iniziare.</div>
      ) : (
        <div className="space-y-3" data-testid="admin-car-list">
          {cars.map((c) => (
            <div key={c.id} className="flex items-center gap-4 rounded-2xl bg-white border border-[var(--line)] p-3 pr-4" data-testid={`admin-car-${c.id}`}>
              <div className="w-24 sm:w-32 aspect-[4/3] rounded-xl overflow-hidden shrink-0"><CarPhoto path={c.foto?.[0]} alt="" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg sm:text-xl font-semibold truncate">{carTitle(c)}</p>
                <p className="text-sm text-[var(--muted)]">{formatPrice(c.prezzo)} · {c.foto?.length ?? 0} foto {c.venduto && <span className="ml-1 text-[var(--red)] font-semibold">· Venduta</span>}</p>
              </div>
              <Link to={`/auto/${c.id}`} className="hidden sm:grid p-2.5 rounded-full hover:bg-[var(--bone)]" aria-label="Vedi" data-testid={`admin-view-${c.id}`}><ExternalLink className="w-4 h-4" /></Link>
              <button onClick={() => setEditing(c)} className="p-2.5 rounded-full hover:bg-[var(--bone)]" aria-label="Modifica" data-testid={`admin-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => setToDelete(c)} className="p-2.5 rounded-full hover:bg-red-50 text-[var(--red)]" aria-label="Elimina" data-testid={`admin-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto bg-[var(--paper)]" data-lenis-prevent data-testid="car-form-sheet">
          <SheetHeader><SheetTitle className="font-display text-2xl">{editing?.id ? "Modifica auto" : "Nuova auto"}</SheetTitle></SheetHeader>
          <div className="mt-6">{editing && <CarForm car={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}</div>
        </SheetContent>
      </Sheet>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa auto?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete && carTitle(toDelete)} verrà rimossa dal sito.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[var(--red)] hover:bg-[var(--red-dark)]" data-testid="delete-confirm-btn">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminApi.get("/auth/me").then(() => setAuthed(true)).catch(() => { setToken(null); setAuthed(false); });
  }, []);
  if (authed === null) return <div className="min-h-screen" />;
  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { setToken(null); setAuthed(false); }} />;
}
